import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Gift, Copy, Check, Users, ShoppingBag, 
  Euro, Share2, MessageCircle, Mail, Loader2
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralPage = () => {
  const { user, token } = useUser();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user && token) {
      fetchReferralData();
    } else {
      setLoading(false);
    }
  }, [user, token]);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API}/user/referral`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success('Codice copiato!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = `https://site-refresh-test-1.preview.emergentagent.com?ref=${referralData?.referral_code}`;

  const shareVia = (platform) => {
    const message = `Usa il mio codice ${referralData?.referral_code} per avere il 10% di sconto su QANTRI! ${shareLink}`;
    
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(message)}`,
      email: `mailto:?subject=Sconto QANTRI per te!&body=${encodeURIComponent(message)}`
    };
    
    window.open(urls[platform], '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="referral-page-guest">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#064E3B] to-[#0A6B4F] text-white py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Gift className="h-16 w-16 mx-auto mb-6 text-[#D4AF37]" />
            <h1 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Programma <span className="italic">Referral</span>
            </h1>
            <p className="text-xl text-white/80 max-w-xl mx-auto">
              Invita i tuoi amici e guadagna con ogni acquisto!
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-6 -mt-8">
          {/* Spiegazione del programma */}
          <div className="bg-white rounded-3xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-light mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Come funziona?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-[#064E3B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-[#064E3B]" />
                </div>
                <h3 className="font-medium mb-2">1. Registrati</h3>
                <p className="text-sm text-neutral-500">Crea un account gratuito e ottieni il tuo codice referral personale</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-[#064E3B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="h-6 w-6 text-[#064E3B]" />
                </div>
                <h3 className="font-medium mb-2">2. Condividi</h3>
                <p className="text-sm text-neutral-500">Invia il tuo codice ad amici e familiari via WhatsApp, Email o social</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Euro className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-medium mb-2">3. Guadagna</h3>
                <p className="text-sm text-neutral-500">Ricevi €5 per ogni amico che acquista. Loro ottengono il 10% di sconto!</p>
              </div>
            </div>

            {/* Vantaggi */}
            <div className="bg-gradient-to-r from-[#064E3B]/5 to-[#D4AF37]/5 rounded-2xl p-6 mb-8">
              <h3 className="font-medium mb-4 text-center">Perché partecipare?</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-[#064E3B]" />
                  <span>Guadagni €5 per ogni referral</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-[#064E3B]" />
                  <span>I tuoi amici ricevono il 10% di sconto</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-[#064E3B]" />
                  <span>Nessun limite ai guadagni</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-[#064E3B]" />
                  <span>Credito utilizzabile subito</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <p className="text-neutral-500 mb-6">
                Registrati ora per ottenere il tuo codice referral personale e iniziare a guadagnare!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button className="bg-[#064E3B] hover:bg-[#043927] text-white rounded-full px-8 py-3 w-full sm:w-auto">
                    Registrati Gratis
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B]/5 rounded-full px-8 py-3 w-full sm:w-auto">
                    Hai già un account? Accedi
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="referral-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#064E3B] to-[#0A6B4F] text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Gift className="h-12 w-12 mx-auto mb-6 text-[#D4AF37]" />
            <h1 className="text-4xl md:text-5xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Invita e <span className="italic">Guadagna</span>
            </h1>
            <p className="text-xl text-white/80 max-w-xl mx-auto">
              Condividi il tuo codice con gli amici. Tu guadagni €5, loro ricevono il 10% di sconto!
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 -mt-12">
        {/* Referral Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-xl p-8 mb-8"
        >
          <h2 className="text-lg font-medium mb-4 text-center">Il tuo codice referral</h2>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="bg-neutral-100 px-8 py-4 rounded-2xl">
              <span className="text-3xl font-bold tracking-wider text-[#064E3B]">
                {referralData?.referral_code || '---'}
              </span>
            </div>
            <Button
              onClick={copyCode}
              variant="outline"
              className="rounded-full"
            >
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </Button>
          </div>

          {/* Share Buttons */}
          <div className="flex justify-center gap-3 mb-8">
            <Button
              onClick={() => shareVia('whatsapp')}
              className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
            <Button
              onClick={() => shareVia('telegram')}
              className="bg-[#0088cc] hover:bg-[#0077b5] text-white rounded-full"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Telegram
            </Button>
            <Button
              onClick={() => shareVia('email')}
              variant="outline"
              className="rounded-full"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-neutral-50 rounded-2xl">
              <Users className="h-6 w-6 text-[#064E3B] mx-auto mb-2" />
              <p className="text-2xl font-bold">{referralData?.referrals_count || 0}</p>
              <p className="text-sm text-neutral-500">Amici invitati</p>
            </div>
            <div className="text-center p-4 bg-neutral-50 rounded-2xl">
              <ShoppingBag className="h-6 w-6 text-[#064E3B] mx-auto mb-2" />
              <p className="text-2xl font-bold">{referralData?.orders_from_referrals || 0}</p>
              <p className="text-sm text-neutral-500">Ordini generati</p>
            </div>
            <div className="text-center p-4 bg-[#D4AF37]/10 rounded-2xl">
              <Euro className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#D4AF37]">€{referralData?.referral_credits || 0}</p>
              <p className="text-sm text-neutral-500">Crediti guadagnati</p>
            </div>
          </div>
        </motion.div>

        {/* How it works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-xl font-medium mb-6 text-center">Come funziona</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#064E3B] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="font-medium mb-2">Condividi il codice</h3>
              <p className="text-sm text-neutral-500">
                Invia il tuo codice referral ad amici e familiari
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#064E3B] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="font-medium mb-2">Loro ottengono il 10%</h3>
              <p className="text-sm text-neutral-500">
                I tuoi amici ricevono il 10% di sconto sul primo ordine
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#D4AF37] text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="font-medium mb-2">Tu guadagni €5</h3>
              <p className="text-sm text-neutral-500">
                Per ogni ordine completato ricevi €5 di credito
              </p>
            </div>
          </div>
        </motion.div>

        {/* Terms */}
        <div className="text-center text-sm text-neutral-400 pb-12">
          <p>I crediti possono essere utilizzati per acquisti futuri su QANTRI.</p>
          <p>Non ci sono limiti al numero di amici che puoi invitare.</p>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
